// src/pages/AdminSessionQuality.js - Dashboard qualité des sessions
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const API_URL = process.env.REACT_APP_API_URL;

function AdminSessionQuality() {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [filter, setFilter] = useState('all'); // all | short | no_prof | no_eleve | incomplete
  const [sortBy, setSortBy] = useState('date_desc');
  const [profFilter, setProfFilter] = useState('all');
  const [profs, setProfs] = useState([]);

  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Authorization': `Bearer ${session?.access_token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: reservations, error: resError } = await supabase
        .from('reservations')
        .select(`
          id, date, heure_debut, heure_fin, duree_minutes, statut,
          duree_reelle, heure_debut_reelle, heure_fin_reelle,
          taux_efficacite, difference_duree,
          prof_joined_at, eleve_joined_at, ended_by,
          professeurs:prof_id (id, nom, prenom, email),
          eleves:eleve_id (id, nom, prenom, email)
        `)
        .in('statut', ['confirmé', 'terminé'])
        .order('date', { ascending: false })
        .limit(200);

      if (resError) throw resError;

      setSessions(reservations || []);

      // Calculer les stats
      const total = reservations?.length || 0;
      const avecDuree = reservations?.filter(r => r.duree_reelle != null) || [];
      const sansProfConnexion = reservations?.filter(r => !r.prof_joined_at && r.heure_debut_reelle) || [];
      const sansEleveConnexion = reservations?.filter(r => !r.eleve_joined_at && r.heure_debut_reelle) || [];
      const courtes = avecDuree.filter(r => r.duree_reelle > 0 && r.duree_reelle < 30 && r.duree_minutes >= 30);
      const completes = avecDuree.filter(r => r.taux_efficacite >= 80);
      const moyenneDuree = avecDuree.length > 0
        ? Math.round(avecDuree.reduce((a, r) => a + (r.duree_reelle || 0), 0) / avecDuree.length)
        : 0;

      // Stats par prof
      const profStats = {};
      reservations?.forEach(r => {
        const profId = r.professeurs?.id;
        if (!profId) return;
        if (!profStats[profId]) {
          profStats[profId] = {
            id: profId,
            nom: `${r.professeurs?.prenom || ''} ${r.professeurs?.nom || ''}`.trim(),
            email: r.professeurs?.email,
            total: 0, courtes: 0, sansCo: 0, totalDuree: 0, count: 0
          };
        }
        profStats[profId].total++;
        if (r.duree_reelle > 0 && r.duree_reelle < 30 && r.duree_minutes >= 30) profStats[profId].courtes++;
        if (!r.prof_joined_at && r.heure_debut_reelle) profStats[profId].sansCo++;
        if (r.duree_reelle) { profStats[profId].totalDuree += r.duree_reelle; profStats[profId].count++; }
      });

      const profList = Object.values(profStats).sort((a, b) => b.courtes - a.courtes);
      setProfs([{ id: 'all', nom: 'Tous les profs' }, ...profList]);

      setStats({
        total, avecDuree: avecDuree.length,
        sansProfConnexion: sansProfConnexion.length,
        sansEleveConnexion: sansEleveConnexion.length,
        courtes: courtes.length,
        completes: completes.length,
        tauxCompletion: total > 0 ? Math.round((completes.length / Math.max(avecDuree.length, 1)) * 100) : 0,
        moyenneDuree,
        profStats: profList
      });

      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getFilteredSessions = () => {
    let filtered = [...sessions];

    if (profFilter !== 'all') {
      filtered = filtered.filter(r => r.professeurs?.id === profFilter);
    }

    switch (filter) {
      case 'short': filtered = filtered.filter(r => r.duree_reelle != null && r.duree_reelle < 30 && r.duree_minutes >= 30); break;
      case 'no_prof': filtered = filtered.filter(r => !r.prof_joined_at && r.heure_debut_reelle); break;
      case 'no_eleve': filtered = filtered.filter(r => !r.eleve_joined_at && r.heure_debut_reelle); break;
      case 'incomplete': filtered = filtered.filter(r => r.taux_efficacite != null && r.taux_efficacite < 80); break;
      case 'completed': filtered = filtered.filter(r => r.taux_efficacite != null && r.taux_efficacite >= 80); break;
      default: break;
    }

    switch (sortBy) {
      case 'date_desc': filtered.sort((a, b) => new Date(b.date) - new Date(a.date)); break;
      case 'date_asc': filtered.sort((a, b) => new Date(a.date) - new Date(b.date)); break;
      case 'duree_asc': filtered.sort((a, b) => (a.duree_reelle || 999) - (b.duree_reelle || 999)); break;
      case 'taux_asc': filtered.sort((a, b) => (a.taux_efficacite || 0) - (b.taux_efficacite || 0)); break;
      default: break;
    }

    return filtered;
  };

  const formatTime = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const getStatusColor = (taux) => {
    if (taux == null) return '#94a3b8';
    if (taux >= 80) return '#22c55e';
    if (taux >= 50) return '#f59e0b';
    return '#ef4444';
  };

  const getStatusLabel = (r) => {
    if (!r.heure_debut_reelle) return { label: 'Non démarrée', color: '#94a3b8' };
    if (!r.prof_joined_at) return { label: 'Prof absent', color: '#ef4444' };
    if (!r.eleve_joined_at) return { label: 'Élève absent', color: '#f59e0b' };
    if (r.taux_efficacite >= 80) return { label: 'Complète', color: '#22c55e' };
    if (r.taux_efficacite >= 50) return { label: 'Partielle', color: '#f59e0b' };
    return { label: 'Courte', color: '#ef4444' };
  };

  const s = { // styles
    page: { minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', fontFamily: 'system-ui, sans-serif', padding: '24px' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 },
    title: { fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0 },
    subtitle: { fontSize: 13, color: '#64748b', margin: '4px 0 0' },
    btn: { padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 },
    btnPrimary: { background: '#3b82f6', color: '#fff' },
    btnGhost: { background: 'rgba(255,255,255,0.05)', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.1)' },
    kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 28 },
    kpi: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '16px 20px' },
    kpiValue: { fontSize: 32, fontWeight: 700, margin: '4px 0' },
    kpiLabel: { fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' },
    section: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: 20, marginBottom: 20 },
    sectionTitle: { fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 },
    filterRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
    filterBtn: (active) => ({
      padding: '6px 14px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.1)',
      background: active ? '#3b82f6' : 'rgba(255,255,255,0.04)',
      color: active ? '#fff' : '#94a3b8', cursor: 'pointer', fontSize: 12, fontWeight: 500
    }),
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid rgba(255,255,255,0.06)' },
    td: { padding: '12px 12px', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle' },
    badge: (color) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: color + '22', color }),
    profCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' },
    alert: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#fca5a5', fontSize: 13 },
  };

  const filteredSessions = getFilteredSessions();

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>📊 Qualité des Sessions</h1>
          <p style={s.subtitle}>
            {lastRefresh ? `Mis à jour ${lastRefresh.toLocaleTimeString('fr-FR')}` : 'Chargement...'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Link to="/admin-dashboard" style={{ ...s.btn, ...s.btnGhost, textDecoration: 'none' }}>← Admin</Link>
          <button style={{ ...s.btn, ...s.btnPrimary }} onClick={fetchData} disabled={loading}>
            {loading ? '⟳' : '↻'} Actualiser
          </button>
        </div>
      </div>

      {error && <div style={s.alert}>❌ {error}</div>}

      {/* KPIs */}
      {stats && (
        <div style={s.kpiGrid}>
          <div style={s.kpi}>
            <div style={s.kpiLabel}>Sessions totales</div>
            <div style={{ ...s.kpiValue, color: '#60a5fa' }}>{stats.total}</div>
          </div>
          <div style={s.kpi}>
            <div style={s.kpiLabel}>Taux complétion</div>
            <div style={{ ...s.kpiValue, color: stats.tauxCompletion >= 80 ? '#22c55e' : '#f59e0b' }}>{stats.tauxCompletion}%</div>
          </div>
          <div style={s.kpi}>
            <div style={s.kpiLabel}>Durée moyenne</div>
            <div style={{ ...s.kpiValue, color: '#a78bfa' }}>{stats.moyenneDuree} min</div>
          </div>
          <div style={s.kpi}>
            <div style={s.kpiLabel}>Sessions courtes</div>
            <div style={{ ...s.kpiValue, color: stats.courtes > 0 ? '#ef4444' : '#22c55e' }}>{stats.courtes}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>{'< 30 min'}</div>
          </div>
          <div style={s.kpi}>
            <div style={s.kpiLabel}>Prof absent</div>
            <div style={{ ...s.kpiValue, color: stats.sansProfConnexion > 0 ? '#ef4444' : '#22c55e' }}>{stats.sansProfConnexion}</div>
          </div>
          <div style={s.kpi}>
            <div style={s.kpiLabel}>Élève absent</div>
            <div style={{ ...s.kpiValue, color: stats.sansEleveConnexion > 0 ? '#f59e0b' : '#22c55e' }}>{stats.sansEleveConnexion}</div>
          </div>
        </div>
      )}

      {/* Alertes */}
      {stats?.sansProfConnexion > 0 && (
        <div style={s.alert}>
          ⚠️ {stats.sansProfConnexion} session(s) où le professeur ne s'est pas connecté — à vérifier
        </div>
      )}
      {stats?.courtes > 0 && (
        <div style={{ ...s.alert, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', color: '#fcd34d' }}>
          ⚠️ {stats.courtes} session(s) de moins de 30 minutes détectées
        </div>
      )}

      {/* Top profs à surveiller */}
      {stats?.profStats?.filter(p => p.courtes > 0 || p.sansCo > 0).length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>⚠️ Professeurs à surveiller</div>
          {stats.profStats.filter(p => p.courtes > 0 || p.sansCo > 0).slice(0, 5).map(prof => (
            <div key={prof.id} style={s.profCard}>
              <div>
                <div style={{ fontWeight: 600, color: '#f1f5f9' }}>{prof.nom}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{prof.email}</div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                {prof.courtes > 0 && <span style={s.badge('#ef4444')}>{prof.courtes} courte(s)</span>}
                {prof.sansCo > 0 && <span style={s.badge('#f59e0b')}>{prof.sansCo} absent(s)</span>}
                <span style={{ fontSize: 12, color: '#64748b' }}>{prof.count > 0 ? `~${Math.round(prof.totalDuree / prof.count)} min moy.` : ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tableau des sessions */}
      <div style={s.section}>
        <div style={s.sectionTitle}>📋 Détail des sessions</div>

        {/* Filtres */}
        <div style={s.filterRow}>
          {[
            { key: 'all', label: `Toutes (${sessions.length})` },
            { key: 'short', label: `Courtes < 30min (${stats?.courtes || 0})` },
            { key: 'no_prof', label: `Prof absent (${stats?.sansProfConnexion || 0})` },
            { key: 'no_eleve', label: `Élève absent (${stats?.sansEleveConnexion || 0})` },
            { key: 'incomplete', label: 'Incomplètes' },
            { key: 'completed', label: 'Complètes' },
          ].map(f => (
            <button key={f.key} style={s.filterBtn(filter === f.key)} onClick={() => setFilter(f.key)}>{f.label}</button>
          ))}
        </div>

        {/* Filtre prof + tri */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <select
            value={profFilter}
            onChange={e => setProfFilter(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#e2e8f0', fontSize: 13 }}
          >
            {profs.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
          </select>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#e2e8f0', fontSize: 13 }}
          >
            <option value="date_desc">Date ↓</option>
            <option value="date_asc">Date ↑</option>
            <option value="duree_asc">Durée ↑</option>
            <option value="taux_asc">Taux ↑</option>
          </select>
          <span style={{ fontSize: 12, color: '#64748b', alignSelf: 'center' }}>{filteredSessions.length} résultat(s)</span>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Chargement...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Date</th>
                  <th style={s.th}>Prof</th>
                  <th style={s.th}>Élève</th>
                  <th style={s.th}>Prévu</th>
                  <th style={s.th}>Prof connecté</th>
                  <th style={s.th}>Élève connecté</th>
                  <th style={s.th}>Durée réelle</th>
                  <th style={s.th}>Taux</th>
                  <th style={s.th}>Fin par</th>
                  <th style={s.th}>Statut</th>
                </tr>
              </thead>
              <tbody>
                {filteredSessions.length === 0 ? (
                  <tr><td colSpan={10} style={{ ...s.td, textAlign: 'center', color: '#64748b', padding: 32 }}>Aucune session</td></tr>
                ) : filteredSessions.map(r => {
                  const status = getStatusLabel(r);
                  return (
                    <tr key={r.id}>
                      <td style={s.td}>
                        <div style={{ fontWeight: 500 }}>{formatDate(r.date)}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{r.heure_debut?.substring(0, 5)}</div>
                      </td>
                      <td style={s.td}>
                        <div>{r.professeurs?.prenom} {r.professeurs?.nom}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{r.professeurs?.email}</div>
                      </td>
                      <td style={s.td}>
                        <div>{r.eleves?.prenom} {r.eleves?.nom}</div>
                      </td>
                      <td style={{ ...s.td, color: '#94a3b8' }}>{r.duree_minutes} min</td>
                      <td style={s.td}>
                        {r.prof_joined_at
                          ? <span style={{ color: '#22c55e' }}>✓ {formatTime(r.prof_joined_at)}</span>
                          : <span style={{ color: '#ef4444' }}>✗ Absent</span>}
                      </td>
                      <td style={s.td}>
                        {r.eleve_joined_at
                          ? <span style={{ color: '#22c55e' }}>✓ {formatTime(r.eleve_joined_at)}</span>
                          : <span style={{ color: '#f59e0b' }}>✗ Absent</span>}
                      </td>
                      <td style={s.td}>
                        {r.duree_reelle != null
                          ? <span style={{ color: getStatusColor(r.taux_efficacite), fontWeight: 600 }}>{r.duree_reelle} min</span>
                          : <span style={{ color: '#64748b' }}>—</span>}
                      </td>
                      <td style={s.td}>
                        {r.taux_efficacite != null
                          ? <span style={{ color: getStatusColor(r.taux_efficacite), fontWeight: 600 }}>{r.taux_efficacite}%</span>
                          : '—'}
                      </td>
                      <td style={s.td}>
                        {r.ended_by
                          ? <span style={s.badge(r.ended_by === 'prof' ? '#a78bfa' : '#60a5fa')}>{r.ended_by}</span>
                          : '—'}
                      </td>
                      <td style={s.td}>
                        <span style={s.badge(status.color)}>{status.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminSessionQuality;
